import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../redux/slices/authSlice";
import { clearQrCode } from "@/redux/slices/qrSlice";
import { ArrowLeft, Home, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getT } from "@/i18n/translations";
import { fetchCustomerData } from "@/redux/slices/customerSlice";


export default function Profile() {
  const t = getT();
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const mobile = localStorage.getItem("mobile") || localStorage.getItem("phone") || localStorage.getItem("phoneNumber") || "";
  const { data: customer } = useSelector((state) => state.customer);
  const customerName = customer?.customer?.firstName || customer?.customer?.name || "";
  const customerState = customer?.customer?.state || "";
  const customerDistrict = customer?.customer?.district || "";
  const customerVillage = customer?.customer?.village || "";
  const customerPincode = customer?.customer?.pin || "";
  const customerAcreage = customer?.customer?.landowned || "";


  useEffect(() => {
      const customerCode = localStorage.getItem("customercode");
      dispatch(fetchCustomerData(customerCode));
    }, []);
  
  const avatar = customerName ? customerName[0].toUpperCase() : mobile ? mobile[0] : "U";

  return (
    <div
className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: '#f0f4f0',
        backgroundImage: 'radial-gradient(circle, #c8d8c8 1px, transparent 1px)',
        backgroundSize: '22px 22px',
      }}
    >
<div className="w-full max-w-md min-h-screen flex flex-col">

        {/* Header */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <ArrowLeft className="w-5 h-5 text-[#1a2e1a]" />
          </button>
          <p className="text-[15px] font-semibold text-[#1a2e1a]">{t.profile_myProfile}</p>
          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <Home className="w-5 h-5 text-[#1a2e1a]" />
          </button>
        </div>

        {/* Logout Modal */}
        {open && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
            <div className="bg-white w-full max-w-sm rounded-2xl p-7 text-center shadow-2xl mx-4">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">👋</span>
              </div>
              <h2 className="text-[18px] font-bold text-[#1a2e1a] mb-2">{t.profile_logoutTitle}</h2>
              <p className="text-[13px] text-gray-400 mb-6 leading-relaxed">{t.profile_logoutMsg}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setOpen(false)}
                  className="flex-1 py-3 rounded-xl text-[14px] font-semibold border-[1.5px] border-[#2d5a2d] text-[#2d5a2d] bg-white active:scale-[0.98]"
                >
                  {t.profile_cancel}
                </button>
                <button
                  onClick={() => { dispatch(clearQrCode()); dispatch(logout()); }}
                  className="flex-1 py-3 rounded-xl text-[14px] font-semibold text-white active:scale-[0.98]"
                  style={{ backgroundColor: '#2d5a2d' }}
                >
                  {t.profile_logoutConfirm}
                </button>
              </div>
            </div>
          </div>
        )}

     <div className="px-4 pb-8 space-y-3 flex-1">

          {/* Avatar + Name + Mobile */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1 flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-[22px] font-bold text-white shrink-0"
              style={{ backgroundColor: '#2d5a2d' }}
            >
              {avatar}
            </div>
            <div>
              <p className="text-[16px] font-bold text-[#1a2e1a]">{customerName || "—"}</p>
              <p className="text-[13px] text-gray-400">{mobile || "—"}</p>
            </div>
          </div>

          {/* Location Details */}
          <div className="bg-white rounded-2xl px-5 py-4 shadow-sm mx-1">
            <p className="text-[10px] font-semibold text-[#8a9e8a] uppercase tracking-widest mb-3">
              {t.profile_locationDetails}
            </p>
            <div className="space-y-2.5">
              {[
                { label: t.profile_state, value: customerState },
                { label: t.profile_district, value: customerDistrict },
                { label: t.profile_village, value: customerVillage },
                { label: t.profile_pincode, value: customerPincode },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center">
                  <p className="text-[13px] text-gray-400">{label}</p>
                  <p className="text-[13px] font-semibold text-[#1a2e1a]">{value || "—"}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Farm Details */}
          <div className="bg-white rounded-2xl px-5 py-4 shadow-sm mx-1">
            <p className="text-[10px] font-semibold text-[#8a9e8a] uppercase tracking-widest mb-3">
              {t.profile_farmDetails}
            </p>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4a7c59" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                <p className="text-[13px] text-gray-400">{t.profile_totalAcreage}</p>
              </div>
              <p className="text-[13px] font-semibold text-[#1a2e1a]">
                {customerAcreage ? `${customerAcreage} ${t.profile_acres}` : "—"}
              </p>
            </div>
          </div>

          {/* Notice */}
          <div
            className="rounded-2xl px-4 py-3 mx-1 flex items-start gap-2"
            style={{ backgroundColor: '#eaf3de', border: '1px solid #a8d08a' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4a7c59" strokeWidth="2" className="mt-0.5 shrink-0">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p className="text-[11px] text-[#4a7c59] leading-relaxed">{t.profile_noticeText}</p>
          </div>

          {/* Back to Dashboard */}
          <button
            onClick={() => navigate("/home")}
            className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform"
            style={{ width: 'calc(100% - 8px)', marginLeft: '4px', backgroundColor: '#2d5a2d' }}
          >
            {t.profile_backToDashboard}
          </button>

          {/* Logout */}
          <button
            onClick={() => setOpen(true)}
            className="w-full py-3.5 rounded-xl text-[14px] font-semibold text-red-500 border-[1.5px] border-red-200 bg-white active:scale-[0.98] transition-transform"
            style={{ width: 'calc(100% - 8px)', marginLeft: '4px' }}
          >
            {t.profile_logout}
          </button>

          


        </div>
         <div className="w-full py-3 border-t border-gray-200 mt-auto">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Gencrest
          </p>
        </div>
      </div>
    </div>
  );
}